import { useState, useEffect } from 'react';

const CLIENTS_KEY = 'LathiNyx_clients_v2';
const PROJECTS_KEY = 'LathiNyx_projects_v1';

function loadClients() { try { return JSON.parse(localStorage.getItem(CLIENTS_KEY)) || []; } catch { return []; } }
function loadProjects() { try { return JSON.parse(localStorage.getItem(PROJECTS_KEY)) || []; } catch { return []; } }

function getClientProjects(clients) {
  const result = [];
  clients.forEach(c => (c.projects || []).forEach(p => result.push({
    id: 'cp_' + c.id + '_' + p.id,
    name: p.title || p.name || 'Untitled',
    client: c.name,
    svc: c.svc || 'Other',
    deadline: p.end || '',
    value: parseFloat(p.value) || 0,
    status: p.status === 'Completed' ? 'done' : p.status === 'In Progress' ? 'active' : 'pending',
    source: 'client',
  })));
  return result;
}

function getLiveStats() {
  const clients = loadClients();
  const ownProjects = loadProjects();
  const clientProjects = getClientProjects(clients);
  const allProjects = [...clientProjects, ...ownProjects];
  const allInvoices = clients.flatMap(c =>
    (c.invoices || []).map(i => ({ ...i, clientName: c.name, svc: c.svc || 'Other' }))
  );
  const totalInvoiced = allInvoices.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalPaid = allInvoices.filter(i => i.status === 'Paid').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const pendingAmt = allInvoices.filter(i => i.status === 'Pending' || i.status === 'Overdue').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const svcRevenue = {};
  allInvoices.filter(i => i.status === 'Paid').forEach(i => {
    svcRevenue[i.svc] = (svcRevenue[i.svc] || 0) + (parseFloat(i.amount) || 0);
  });
  const today = new Date();
  const upcoming = allProjects
    .filter(p => p.deadline && p.status !== 'done')
    .map(p => ({ ...p, daysLeft: Math.ceil((new Date(p.deadline) - today) / 86400000) }))
    .filter(p => p.daysLeft >= 0)
    .sort((a, b) => a.daysLeft - b.daysLeft)
    .slice(0, 5);
  const recentInvoices = [...allInvoices]
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    .slice(0, 6);
  return {
    totalClients: clients.length,
    activeProjects: allProjects.filter(p => p.status === 'active').length,
    doneProjects: allProjects.filter(p => p.status === 'done').length,
    pendingProjects: allProjects.filter(p => p.status === 'pending').length,
    totalInvoiced, totalPaid, pendingAmt,
    svcRevenue, upcoming, recentInvoices,
    allProjects: allProjects.slice(0, 8),
  };
}

function fmtAmt(n) {
  n = parseFloat(n) || 0;
  if (n >= 100000) return String.fromCharCode(8377) + (n / 100000).toFixed(1) + 'L';
  if (n >= 1000) return String.fromCharCode(8377) + (n / 1000).toFixed(0) + 'K';
  return String.fromCharCode(8377) + n;
}

const ST_CLASS = { Paid: 's-paid', Pending: 's-pending', Overdue: 's-overdue', Cancelled: 's-draft', Draft: 's-draft' };
const SVC_TAG = { 'Web Dev': 'tw', 'AI Training': 'ta', 'Research': 'tr-tag' };

export default function Overview({ setPage }) {
  const [stats, setStats] = useState(() => getLiveStats());

  useEffect(() => {
    const refresh = () => setStats(getLiveStats());
    window.addEventListener('storage', refresh);
    const interval = setInterval(refresh, 2000);
    return () => { window.removeEventListener('storage', refresh); clearInterval(interval); };
  }, []);

  const paidPct = stats.totalInvoiced > 0 ? Math.round((stats.totalPaid / stats.totalInvoiced) * 100) : 0;
  const svcEntries = Object.entries(stats.svcRevenue).sort((a, b) => b[1] - a[1]);
  const maxSvc = svcEntries.length > 0 ? svcEntries[0][1] : 1;

  return (
    <div>
      <div className="d-metrics">
        <div className="d-mcard green">
          <div className="d-mlabel">Total Paid</div>
          <div className="d-mval">{fmtAmt(stats.totalPaid)}</div>
          <div className="d-msub">{paidPct}% of {fmtAmt(stats.totalInvoiced)} invoiced</div>
        </div>
        <div className="d-mcard yellow">
          <div className="d-mlabel">Amount Due</div>
          <div className="d-mval">{fmtAmt(stats.pendingAmt)}</div>
          <div className="d-msub">Pending + overdue</div>
        </div>
        <div className="d-mcard blue">
          <div className="d-mlabel">Active Projects</div>
          <div className="d-mval">{stats.activeProjects}</div>
          <div className="d-msub">{stats.pendingProjects} pending</div>
        </div>
        <div className="d-mcard pink">
          <div className="d-mlabel">Total Clients</div>
          <div className="d-mval">{stats.totalClients}</div>
          <div className="d-msub">Saved in CRM</div>
        </div>
      </div>
      <div className="d-metrics d-metrics-3" style={{ marginBottom: 16 }}>
        <div className="d-mcard blue"><div className="d-mlabel">Active</div><div className="d-mval">{stats.activeProjects}</div></div>
        <div className="d-mcard green"><div className="d-mlabel">Completed</div><div className="d-mval">{stats.doneProjects}</div></div>
        <div className="d-mcard yellow"><div className="d-mlabel">Pending</div><div className="d-mval">{stats.pendingProjects}</div></div>
      </div>
      <div className="d-two-col">
        <div className="d-card">
          <div className="d-ctitle">Recent Invoices <button className="d-btn d-btn-sm" onClick={() => setPage('clients')} style={{ fontSize: 11 }}>View clients</button></div>
          {stats.recentInvoices.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '12px 0' }}>No invoices yet.</div>
          ) : (
            <table className="d-table">
              <thead><tr><th>Client</th><th>Service</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>{stats.recentInvoices.map((inv, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{inv.clientName}</td>
                  <td><span className={'d-tag ' + (SVC_TAG[inv.svc] || 'tw')}>{inv.svc}</span></td>
                  <td style={{ fontWeight: 600 }}>{fmtAmt(inv.amount)}</td>
                  <td><span className={'d-st ' + (ST_CLASS[inv.status] || 's-draft')}>{inv.status}</span></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
        <div>
          <div className="d-card">
            <div className="d-ctitle">Upcoming Deadlines</div>
            {stats.upcoming.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: 13 }}>No upcoming deadlines.</div>
            ) : stats.upcoming.map((p, i) => (
              <div key={i} className="d-hrow">
                <div className="d-hdot" style={{ background: p.daysLeft <= 3 ? '#e11d48' : p.daysLeft <= 7 ? '#d97706' : '#2563eb' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#111827' }}>{p.client} - {p.name}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{p.deadline} - {p.svc} <span style={{ marginLeft: 6, fontWeight: 600, color: p.daysLeft <= 3 ? '#e11d48' : '#d97706' }}>{p.daysLeft === 0 ? 'Today' : p.daysLeft + 'd left'}</span></div>
                </div>
              </div>
            ))}
          </div>
          <div className="d-card">
            <div className="d-ctitle">Revenue by Service</div>
            {svcEntries.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: 13 }}>No paid invoices yet.</div>
            ) : svcEntries.map(([svc, val]) => (
              <div key={svc} className="d-sbar-row">
                <span style={{ fontSize: 12, color: '#374151', minWidth: 90 }}>{svc}</span>
                <div className="d-sbar-wrap"><div className="d-sbar" style={{ width: Math.round((val / maxSvc) * 100) + '%', background: '#2563eb' }} /></div>
                <span style={{ fontSize: 12, fontWeight: 600, minWidth: 60, textAlign: 'right' }}>{fmtAmt(val)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="d-card">
        <div className="d-ctitle">Project Tracker <button className="d-btn d-btn-sm" onClick={() => setPage('projects')} style={{ fontSize: 11 }}>View all</button></div>
        {stats.allProjects.length === 0 ? (
          <div style={{ color: '#9ca3af', textAlign: 'center', padding: 24, fontSize: 13 }}>No projects yet.</div>
        ) : (
          <table className="d-table">
            <thead><tr><th>Project</th><th>Client</th><th>Service</th><th>Deadline</th><th>Value</th><th>Status</th></tr></thead>
            <tbody>{stats.allProjects.map((p, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{p.name}</td>
                <td style={{ color: '#374151' }}>{p.client}</td>
                <td><span className={'d-tag ' + (SVC_TAG[p.svc] || 'tw')}>{p.svc || 'Other'}</span></td>
                <td style={{ color: '#6b7280', fontSize: 12 }}>{p.deadline || '-'}</td>
                <td style={{ fontWeight: 600 }}>{p.value ? fmtAmt(p.value) : '-'}</td>
                <td><span className={'d-st ' + (p.status === 'done' ? 's-done' : p.status === 'active' ? 's-active' : 's-pending')}>{p.status === 'done' ? 'Done' : p.status === 'active' ? 'Active' : 'Pending'}</span></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
